/**
 * /app/facturacion — comprobantes electrónicos (ARCA / TusFacturas) + acceso a config.
 * No lista tickets HIP simulados: esos no van a sm_invoices ni a AFIP.
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { ExternalLink, FileText, Loader2, Settings2 } from "lucide-react";
import { CrudShellHeader } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { FacturacionTour } from "@/components/ProductTour";
import { Button } from "@/components/ui/button";
import { apiGetJson, clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import {
  INVOICING_PATHS,
  InvoicingDisclaimer,
  InvoicingInfoTip,
  InvoicingPathsLegend,
} from "@/components/invoicing/InvoicingPathsHelp";

export const Route = createFileRoute("/app/facturacion")({
  component: FacturacionPage,
  head: () => ({ meta: [{ title: "Facturación — Structura Market" }] }),
});

type InvoiceRow = {
  id: string;
  orderId: string | null;
  provider?: string;
  status: string;
  comprobanteTipo: string | null;
  comprobanteNro: string | null;
  cae: string | null;
  total: number | null;
  pdfUrl: string | null;
  ticketUrl: string | null;
  customerName: string | null;
  errorMessage: string | null;
  issuedAt: string | null;
  createdAt: string;
};

type ProviderSnap = {
  enabled: boolean;
  configured: boolean;
};

function providerLabel(provider?: string | null): string {
  if (provider === "afip_direct") return "ARCA";
  if (provider === "tusfacturas") return "TusFacturas";
  return provider || "—";
}

function FacturacionPage() {
  const tid = useWorkspaceTenantId();
  const { productFlags } = useTenantContext();
  const flagAfip = productFlags?.invoicing_afip_direct === true;
  const flagTf = productFlags?.invoicing_tusfacturas === true;
  const entitled = flagAfip || flagTf;
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [afip, setAfip] = useState<ProviderSnap | null>(null);
  const [tf, setTf] = useState<ProviderSnap | null>(null);

  const load = useCallback(async () => {
    if (!tid || !entitled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const tasks: Promise<void>[] = [
        (async () => {
          const r = await apiGetJson<{ invoices: InvoiceRow[] }>("/api/tenant/invoices", tid);
          setInvoices(r.invoices ?? []);
        })(),
      ];
      if (flagAfip) {
        tasks.push(
          (async () => {
            try {
              const r = await apiGetJson<{ config: ProviderSnap | null }>("/api/tenant/afip-direct", tid);
              setAfip(r.config ? { enabled: r.config.enabled, configured: r.config.configured } : null);
            } catch {
              setAfip(null);
            }
          })(),
        );
      }
      if (flagTf) {
        tasks.push(
          (async () => {
            try {
              const r = await apiGetJson<{ config: ProviderSnap | null }>("/api/tenant/invoicing", tid);
              setTf(r.config ? { enabled: r.config.enabled, configured: r.config.configured } : null);
            } catch {
              setTf(null);
            }
          })(),
        );
      }
      await Promise.all(tasks);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudieron cargar comprobantes");
    } finally {
      setLoading(false);
    }
  }, [tid, entitled, flagAfip, flagTf]);

  useEffect(() => {
    void load();
  }, [load]);

  // SCRUM-431: ARCA directo no tiene pdf_url (a diferencia de TusFacturas) —
  // se genera server-side on-demand, por eso necesita fetch+blob en vez de
  // un <a href> plano (la ruta exige el header X-Tenant-Id).
  const downloadArcaPdf = async (invoiceId: string) => {
    if (!tid) return;
    try {
      const res = await fetch(`/api/invoices/${invoiceId}/pdf`, {
        credentials: "include",
        headers: clientRequestHeaders(tid),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("No se pudo generar el PDF");
    }
  };

  const afipReady = Boolean(flagAfip && afip?.enabled && afip?.configured);
  const tfReady = Boolean(flagTf && tf?.enabled && tf?.configured);

  if (!entitled) {
    return (
      <div>
        <CrudShellHeader title="Facturación" description="Comprobantes electrónicos AFIP/ARCA" />
        <ClinicalPageShell offset="none" className="pt-6">
          <p className="text-sm text-muted-foreground max-w-lg">
            Este módulo requiere facturación electrónica (ARCA directo o TusFacturasAPP) en tu plan
            o activado por Structura.
          </p>
        </ClinicalPageShell>
      </div>
    );
  }

  return (
    <div>
      <FacturacionTour />
      <CrudShellHeader
        title="Facturación"
        description="Solo electrónicos (ARCA / TusFacturas). No lista tickets del controlador fiscal HIP."
        actions={
          <Button variant="outline" size="sm" className="gap-2" asChild>
            <Link to="/app/settings" search={{ tab: "facturacion" }}>
              <Settings2 className="h-4 w-4" /> Configurar
            </Link>
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="pt-6 space-y-4">
        <MerchantExperienceBar screenId="facturacion" />
        <div className="flex items-start gap-2">
          <InvoicingDisclaimer variant="neutral" className="flex-1">
            {INVOICING_PATHS.listPageBanner}
          </InvoicingDisclaimer>
          <InvoicingInfoTip label="Facturación vs HIP" side="left">
            {INVOICING_PATHS.hipTooltip}
          </InvoicingInfoTip>
        </div>
        <InvoicingPathsLegend />
        <div className="flex flex-wrap gap-2 text-xs">
          {flagAfip ? (
            <span
              className={
                afipReady
                  ? "rounded-md bg-emerald-50 px-2 py-1 text-emerald-800 border border-emerald-200"
                  : "rounded-md bg-amber-50 px-2 py-1 text-amber-900 border border-amber-200"
              }
            >
              ARCA: {afipReady ? "listo" : "sin configurar / desactivado"}
            </span>
          ) : null}
          {flagTf ? (
            <span
              className={
                tfReady
                  ? "rounded-md bg-emerald-50 px-2 py-1 text-emerald-800 border border-emerald-200"
                  : "rounded-md bg-muted px-2 py-1 text-muted-foreground border"
              }
            >
              TusFacturas: {tfReady ? "listo" : "sin configurar"}
            </span>
          ) : null}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : invoices.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center space-y-3 max-w-lg mx-auto">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Todavía no hay comprobantes electrónicos. Emití desde el detalle de un pedido con{" "}
              <strong className="font-medium text-foreground">Emitir factura electrónica</strong>.
            </p>
            <p className="text-xs text-muted-foreground">
              Si en el pedido ves un ticket con etiqueta <span className="text-amber-700">Simulado</span>{" "}
              o CAE <code className="text-[10px]">SIM-CAE-…</code>, eso es el controlador fiscal HIP
              local: no llegó a ARCA y por eso no aparece acá.
            </p>
            {!afipReady && !tfReady ? (
              <p className="text-xs text-amber-800">
                Ningún proveedor está listo. Configurá ARCA o TusFacturas en Ajustes → Facturación.
              </p>
            ) : null}
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/pedidos">Ir a pedidos</Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/settings" search={{ tab: "facturacion" }}>
                  Configurar
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Proveedor</th>
                  <th className="px-3 py-2 font-medium">Comprobante</th>
                  <th className="px-3 py-2 font-medium">Cliente</th>
                  <th className="px-3 py-2 font-medium">Total</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                  <th className="px-3 py-2 font-medium">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {new Date(inv.issuedAt || inv.createdAt).toLocaleString("es-AR", { hour12: false })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{providerLabel(inv.provider)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {inv.comprobanteTipo ?? "—"} {inv.comprobanteNro ?? ""}
                      </div>
                      {inv.cae ? (
                        <div
                          className={
                            inv.cae.startsWith("SIM-")
                              ? "text-xs text-amber-700"
                              : "text-xs text-muted-foreground"
                          }
                        >
                          CAE {inv.cae}
                          {inv.cae.startsWith("SIM-") ? " (simulado)" : ""}
                        </div>
                      ) : (
                        <div className="text-xs text-amber-600">Sin CAE (DEV / pendiente)</div>
                      )}
                    </td>
                    <td className="px-3 py-2">{inv.customerName ?? "—"}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {inv.total != null
                        ? inv.total.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          inv.status === "issued"
                            ? "text-emerald-600"
                            : inv.status === "error"
                              ? "text-rose-600"
                              : "text-muted-foreground"
                        }
                      >
                        {inv.status}
                      </span>
                      {inv.errorMessage ? (
                        <p className="text-[11px] text-rose-600 max-w-[220px] truncate" title={inv.errorMessage}>
                          {inv.errorMessage}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {inv.pdfUrl ? (
                        <a
                          href={inv.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-foreground underline"
                        >
                          PDF <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : inv.provider === "afip_direct" && inv.status === "issued" && inv.cae ? (
                        <button
                          type="button"
                          onClick={() => void downloadArcaPdf(inv.id)}
                          className="inline-flex items-center gap-1 text-foreground underline"
                        >
                          PDF <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ClinicalPageShell>
    </div>
  );
}
